import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Modal } from '@symphenia/ui-components';
import { usersApi } from '@/lib/users/users.api';
import { rolesApi, type RoleView } from '@/lib/roles/roles.api';
import { apiErrorText } from '@/lib/api-error';

const eingabe =
  'w-full rounded-md border border-input bg-background px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring';
const beschriftung = 'mb-1 block text-sm font-medium text-foreground';

/**
 * AP-38-F — **Mitarbeiter ohne Zugang anlegen** (AP-36 Fall 2, Knopf „Anlegen").
 *
 * Eine Mitgliedschaft ohne Anmeldekonto: Er meldet sich nie an, steht aber in
 * jeder Mitarbeiterliste und zählt als Benutzer. Die Adresse ist freiwillig —
 * Werkstattpersonal hat oft keine. Zugang geben lässt sich später.
 */
export function OhneZugangDialog({ open, onClose, onSuccess }: { open: boolean; onClose: () => void; onSuccess: () => void }) {
  const { t } = useTranslation();
  const [form, setForm] = useState({ firstName: '', lastName: '', email: '', roleKey: 'employee' });
  const [rollen, setRollen] = useState<RoleView[]>([]);
  const [laeuft, setLaeuft] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setForm({ firstName: '', lastName: '', email: '', roleKey: 'employee' });
    setFehler(null);
    rolesApi.list().then(setRollen).catch(() => undefined);
  }, [open]);

  if (!open) return null;

  const anlegen = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.firstName.trim() || !form.lastName.trim()) {
      setFehler(t('users.ohneZugang.nameFehlt'));
      return;
    }
    setLaeuft(true);
    setFehler(null);
    try {
      await usersApi.createWithoutAccess({
        firstName: form.firstName.trim(),
        lastName: form.lastName.trim(),
        email: form.email.trim() || null,
        roleKey: form.roleKey,
      });
      onSuccess();
      onClose();
    } catch (err) {
      setFehler(apiErrorText(err, t('common.error')));
    } finally {
      setLaeuft(false);
    }
  };

  return (
    <Modal isOpen onClose={onClose} size="md" className="max-w-md" hideCloseButton>
      <div className="mb-4 flex items-center justify-between">
        <h2 className="text-lg font-semibold text-foreground">{t('users.ohneZugang.titel')}</h2>
        <button onClick={onClose} className="rounded p-1 text-muted-foreground hover:bg-muted hover:text-foreground" aria-label={t('common.cancel')}>
          ✕
        </button>
      </div>
      <form onSubmit={anlegen} className="space-y-4">
        <p className="text-sm text-muted-foreground">{t('users.ohneZugang.hinweis')}</p>
        {fehler && (
          <p className="rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive">{fehler}</p>
        )}
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className={beschriftung} htmlFor="oz-vorname">{t('users.firstName')} *</label>
            <input id="oz-vorname" autoFocus className={eingabe} value={form.firstName} onChange={(e) => setForm((f) => ({ ...f, firstName: e.target.value }))} />
          </div>
          <div>
            <label className={beschriftung} htmlFor="oz-nachname">{t('users.lastName')} *</label>
            <input id="oz-nachname" className={eingabe} value={form.lastName} onChange={(e) => setForm((f) => ({ ...f, lastName: e.target.value }))} />
          </div>
        </div>
        <div>
          <label className={beschriftung} htmlFor="oz-email">{t('auth.email')}</label>
          <input
            id="oz-email"
            type="email"
            className={eingabe}
            value={form.email}
            onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
            placeholder={t('users.ohneZugang.emailOptional')}
          />
        </div>
        <div>
          <label className={beschriftung} htmlFor="oz-rolle">{t('users.role')}</label>
          <select id="oz-rolle" className={eingabe} value={form.roleKey} onChange={(e) => setForm((f) => ({ ...f, roleKey: e.target.value }))}>
            {rollen.map((r) => (
              <option key={r.key} value={r.key}>
                {r.isSystem ? t(`roles.templates.${r.templateKey}.name`, { defaultValue: r.name }) : r.name}
              </option>
            ))}
          </select>
          <p className="mt-1 text-xs text-muted-foreground">{t('users.ohneZugang.rolleHinweis')}</p>
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <button type="button" onClick={onClose} className="rounded-md border border-input px-4 py-2 text-sm text-foreground hover:bg-muted">
            {t('common.cancel')}
          </button>
          <button type="submit" disabled={laeuft} className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50">
            {laeuft ? t('common.loading') : t('users.ohneZugang.anlegen')}
          </button>
        </div>
      </form>
    </Modal>
  );
}
